/**
 * MobrioWishlist — Bloque A (theme-only)
 * Tienda: mobrio.es · Tema: Dawn 15.4.0 · Cuentas: NEW_CUSTOMER_ACCOUNTS
 *
 * FUENTE DE VERDAD: product.id numérico (string).
 *   Motivo: el Bloque B usará un metafield de cliente `mobrio.wishlist` de tipo
 *   `list.product_reference`, que referencia productos POR ID. Guardar IDs aquí
 *   evita cualquier traducción handle<->ID entre el tema y la extensión de cuenta.
 *
 *   Para PINTAR las cards en la página de favoritos del tema usamos el endpoint
 *   público de Shopify /products/{handle}.js (necesita handle), por eso cada botón
 *   expone TAMBIÉN data-product-handle. Pero el array que se guarda/sincroniza son IDs.
 *   Guardamos pares {id, handle} en localStorage para poder pintar sin depender de
 *   una llamada extra de resolución id->handle.
 *
 * Un único objeto global: window.MobrioWishlist.
 * Evento global: 'mobrio:wishlist:updated'.
 * Sin librerías externas. Sin jQuery/React/Vue. Sin polling.
 *
 * FRONTERA CON EL BLOQUE B (contrato):
 *   - sync()        : al iniciar sesión, fusiona local <-> cuenta (idempotente).
 *   - pullAccount() : lee IDs del metafield (inyectados por Liquid en config.accountItems).
 *   - pushAccount() : HOY es un no-op seguro (Bloque A). En el Bloque B se implementa
 *                     con la Customer Account UI Extension (mutation metafieldsSet vía
 *                     Customer Account API). El tema NO escribe el metafield directamente.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'mobrio_wishlist_v2';       // v2: guardamos {id, handle}
  var EVENT_NAME = 'mobrio:wishlist:updated';
  var SYNCED_FLAG_KEY = 'mobrio_wishlist_synced_customer';
  var PRODUCT_CACHE = Object.create(null);

  // -------------------------------------------------------------------------
  // Config inyectada desde Liquid (snippet mobrio-wishlist-init.liquid).
  //   window.MobrioWishlistConfig = {
  //     customer: { id: 123 } | null,
  //     accountItems: ['7712345','7798765'],   // IDs del metafield (Bloque B)
  //     favoritesUrl: '/pages/favoritos',
  //     strings: { ... }
  //   }
  // -------------------------------------------------------------------------
  function getConfig() {
    return window.MobrioWishlistConfig || {};
  }

  // -------------------------------------------------------------------------
  // Persistencia local. Guardamos array de objetos {id, handle}.
  // Toleramos corrupción, JSON inválido, duplicados y el formato v1 antiguo.
  // -------------------------------------------------------------------------
  function normalizeEntry(entry) {
    if (!entry) return null;
    // Formato antiguo (v1) o suelto: string de id o de handle.
    if (typeof entry === 'string') {
      // No sabemos si es id o handle; si es todo dígitos, es id.
      if (/^\d+$/.test(entry)) return { id: entry, handle: null };
      return { id: null, handle: entry };
    }
    if (typeof entry === 'object') {
      var id = entry.id != null ? String(entry.id) : null;
      var handle = entry.handle != null ? String(entry.handle) : null;
      if (!id && !handle) return null;
      return { id: id, handle: handle };
    }
    return null;
  }

  function dedupe(list) {
    var seen = Object.create(null);
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var e = normalizeEntry(list[i]);
      if (!e) continue;
      var key = e.id || ('h:' + e.handle);
      if (seen[key]) {
        // Si ya existe pero el nuevo aporta handle que faltaba, lo completamos.
        var prev = seen[key];
        if (!prev.handle && e.handle) prev.handle = e.handle;
        continue;
      }
      seen[key] = e;
      out.push(e);
    }
    return out;
  }

  function readLocal() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return dedupe(parsed);
    } catch (err) {
      return [];
    }
  }

  function writeLocal(list) {
    var deduped = dedupe(list);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(deduped));
    } catch (err) {
      /* almacenamiento lleno o bloqueado: seguimos en memoria */
    }
    return deduped;
  }

  // -------------------------------------------------------------------------
  // Clase principal
  // -------------------------------------------------------------------------
  function MobrioWishlistClass() {
    this._items = readLocal();   // array de {id, handle}
    this._syncing = false;
    this._bindStorageEvents();
  }

  var P = MobrioWishlistClass.prototype;

  // --- Lectura -------------------------------------------------------------
  P.getItems = function () {
    return this._items.slice();
  };

  // IDs "puros", que es lo que el Bloque B necesita.
  P.getIds = function () {
    return this._items.map(function (e) { return e.id; }).filter(Boolean);
  };

  P._indexOf = function (id, handle) {
    id = id != null ? String(id) : null;
    handle = handle != null ? String(handle) : null;
    for (var i = 0; i < this._items.length; i++) {
      var e = this._items[i];
      if (id && e.id && e.id === id) return i;
      if (handle && e.handle && e.handle === handle) return i;
    }
    return -1;
  };

  P.has = function (id, handle) {
    return this._indexOf(id, handle) !== -1;
  };

  P.count = function () {
    return this._items.length;
  };

  // --- Escritura -----------------------------------------------------------
  P.add = function (id, handle) {
    if (id == null && handle == null) return;
    if (this._indexOf(id, handle) !== -1) return;
    this._items.push(normalizeEntry({ id: id, handle: handle }));
    this._persist();
    this._emitUpdate('add', id, handle);
    this._pushIfLoggedIn();
  };

  P.remove = function (id, handle) {
    var idx = this._indexOf(id, handle);
    if (idx === -1) return;
    this._items.splice(idx, 1);
    this._persist();
    this._emitUpdate('remove', id, handle);
    this._pushIfLoggedIn();
  };

  P.toggle = function (id, handle) {
    if (this.has(id, handle)) {
      this.remove(id, handle);
      return false;
    }
    this.add(id, handle);
    return true;
  };

  P._persist = function () {
    this._items = writeLocal(this._items);
  };

  P._emitUpdate = function (action, id, handle) {
    var detail = {
      action: action || null,
      id: id != null ? String(id) : null,
      handle: handle != null ? String(handle) : null,
      items: this.getItems(),
      ids: this.getIds(),
      count: this.count()
    };
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: detail }));
  };

  // -------------------------------------------------------------------------
  // Sincronización con la cuenta (contrato con el Bloque B)
  // -------------------------------------------------------------------------
  /**
   * sync(): se llama una vez por carga de página.
   *   - Sin cliente logueado -> no hace nada (el visitante vive en localStorage).
   *   - Con cliente logueado -> fusiona local + cuenta (config.accountItems),
   *     sin duplicados, y si hubo cambios respecto a la cuenta, empuja el
   *     resultado con pushAccount() (no-op en Bloque A; real en Bloque B).
   * Idempotente: si ya se sincronizó este cliente y no hay diferencias, no repite.
   */
  P.sync = function () {
    var config = getConfig();
    var customer = config.customer;
    if (!customer || !customer.id) {
      return Promise.resolve(this.getItems());
    }

    var accountIds = Array.isArray(config.accountItems)
      ? config.accountItems.map(String).filter(Boolean)
      : [];

    // Fusión: partimos de lo local y añadimos lo que la cuenta tuviera y falte.
    var localIds = this.getIds();
    var mergedItems = this._items.slice();
    for (var i = 0; i < accountIds.length; i++) {
      if (localIds.indexOf(accountIds[i]) === -1) {
        // La cuenta tiene un ID que local no tenía: lo añadimos (handle se
        // resolverá al pintar la página de favoritos vía /products/{handle}.js,
        // o cuando la card correspondiente aparezca en el catálogo).
        mergedItems.push({ id: accountIds[i], handle: null });
      }
    }
    this._items = writeLocal(mergedItems);

    var mergedIds = this.getIds();
    var differsFromAccount =
      mergedIds.length !== accountIds.length ||
      mergedIds.some(function (id) { return accountIds.indexOf(id) === -1; });

    this._emitUpdate('sync', null, null);

    var alreadySynced = null;
    try {
      alreadySynced = window.sessionStorage.getItem(SYNCED_FLAG_KEY);
    } catch (err) { /* no-op */ }

    if (!differsFromAccount && alreadySynced === String(customer.id)) {
      return Promise.resolve(this.getItems());
    }

    return this.pushAccount().then(function () {
      try { window.sessionStorage.setItem(SYNCED_FLAG_KEY, String(customer.id)); }
      catch (err) { /* no-op */ }
    });
  };

  P._pushIfLoggedIn = function () {
    var config = getConfig();
    if (config.customer && config.customer.id) this.pushAccount();
  };

  /**
   * pushAccount(): escribe los IDs actuales en la cuenta.
   *
   *   BLOQUE A: no-op seguro. No existe forma soportada de escribir un metafield
   *   de cliente desde JS público del tema en New Customer Accounts, y NO usamos
   *   Admin token ni backend. Así que aquí solo dejamos el gancho.
   *
   *   BLOQUE B: la escritura la hace la Customer Account UI Extension con la
   *   mutation `metafieldsSet` vía Customer Account API. El tema NO escribe.
   *   Este método puede quedarse como no-op incluso con el Bloque B activo,
   *   porque la extensión es la dueña de la escritura; el tema solo LEE
   *   (config.accountItems) y fusiona en sync().
   */
  P.pushAccount = function () {
    // Gancho reservado para el Bloque B. Intencionadamente vacío en el A.
    return Promise.resolve();
  };

  /**
   * clearLocal(): en logout, limpia el estado local para no arrastrar los
   * favoritos de un cliente al siguiente que use el mismo navegador.
   */
  P.clearLocal = function () {
    this._items = writeLocal([]);
    try { window.sessionStorage.removeItem(SYNCED_FLAG_KEY); }
    catch (err) { /* no-op */ }
    this._emitUpdate('clear', null, null);
  };

  // -------------------------------------------------------------------------
  // Multi-pestaña
  // -------------------------------------------------------------------------
  P._bindStorageEvents = function () {
    var self = this;
    window.addEventListener('storage', function (event) {
      if (event.key !== STORAGE_KEY) return;
      self._items = readLocal();
      self._emitUpdate('external-sync', null, null);
    });
  };

  // -------------------------------------------------------------------------
  // Render de estado en botones ya presentes en el DOM
  // -------------------------------------------------------------------------
  P.renderState = function (root) {
    var scope = root || document;
    var buttons = scope.querySelectorAll('.mobrio-wishlist-button[data-product-id], .mobrio-wishlist-button[data-product-handle]');
    for (var i = 0; i < buttons.length; i++) {
      this._applyButtonState(buttons[i]);
    }
    this._renderCount(scope);
  };

  P._applyButtonState = function (button) {
    var id = button.getAttribute('data-product-id');
    var handle = button.getAttribute('data-product-handle');
    var active = this.has(id, handle);
    button.classList.toggle('mobrio-wishlist-button--active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    var addLabel = button.getAttribute('data-label-add') || 'Añadir a favoritos';
    var removeLabel = button.getAttribute('data-label-remove') || 'Eliminar de favoritos';
    button.setAttribute('aria-label', active ? removeLabel : addLabel);
  };

  P._renderCount = function (scope) {
    var count = this.count();
    var counters = (scope || document).querySelectorAll('.mobrio-wishlist-count');
    for (var i = 0; i < counters.length; i++) {
      var el = counters[i];
      el.textContent = count > 0 ? String(count) : '';
      el.classList.toggle('mobrio-wishlist-count--hidden', count === 0);
    }
    var pageCount = (scope || document).querySelector('[data-mobrio-wishlist-page-count]');
    if (pageCount) pageCount.hidden = count === 0;
  };

  // -------------------------------------------------------------------------
  // Página "Tus favoritos": render client-side desde /products/{handle}.js
  // -------------------------------------------------------------------------
  function fetchProductByHandle(handle) {
    if (!handle) return Promise.resolve(null);
    if (PRODUCT_CACHE[handle]) return PRODUCT_CACHE[handle];
    PRODUCT_CACHE[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) { if (!res.ok) throw new Error('not-found'); return res.json(); })
      .catch(function () { return null; });
    return PRODUCT_CACHE[handle];
  }

  // Resuelve handle a partir de id cuando falta, usando el endpoint de búsqueda
  // de Shopify por id de producto. Solo se usa para IDs que llegaron de la cuenta
  // sin handle (caso: favorito guardado en otro dispositivo).
  function resolveHandleById(id) {
    if (!id) return Promise.resolve(null);
    var cacheKey = 'id:' + id;
    if (PRODUCT_CACHE[cacheKey]) return PRODUCT_CACHE[cacheKey];
    // /recommendations no sirve aquí; usamos el endpoint de producto por id
    // a través de la Storefront pública: no existe /products/{id}.js, así que
    // recurrimos a la API de búsqueda de predicción por id no es fiable.
    // Estrategia robusta: pedimos la ficha vía el endpoint de producto usando
    // el permalink /products.json no está disponible en storefront. Por eso,
    // cuando solo tenemos id (favorito de otro dispositivo), intentamos el
    // endpoint /products/{id} que Shopify redirige a la ficha y devuelve 404 en .js.
    // -> En la práctica, el Bloque B nos dará el handle junto al id (lo añadiremos
    //    al metafield como referencia, y Liquid puede exponer handle+id en init).
    //    Mientras tanto, si solo hay id sin handle, lo omitimos del pintado.
    PRODUCT_CACHE[cacheKey] = Promise.resolve(null);
    return PRODUCT_CACHE[cacheKey];
  }

  function escapeHtml(v) {
    return String(v).replace(/[&<>"']/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m];
    });
  }

  function formatMoney(cents) {
    return (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
  }

  function buildCardHtml(product, strings) {
    var variant = product.variants && product.variants[0];
    var available = product.available;
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var imageUrl = image ? image + (image.indexOf('?') === -1 ? '?width=533' : '&width=533') : '';

    var badge = available
      ? ''
      : '<span class="mobrio-wishlist-card__badge">' + escapeHtml(strings.soldOut) + '</span>';

    var priceHtml = product.price_varies
      ? escapeHtml(strings.from) + ' ' + formatMoney(product.price_min)
      : formatMoney(product.price);

    var addButton =
      product.variants.length > 1
        ? '<a href="' + product.url + '" class="mobrio-wishlist-card__add">' +
            escapeHtml(strings.chooseOptions) + '</a>'
        : '<button type="button" class="mobrio-wishlist-card__add" data-add-variant-id="' +
            variant.id + '"' + (available ? '' : ' disabled') + '>' +
            escapeHtml(available ? strings.addToCart : strings.soldOut) + '</button>';

    return (
      '<div class="mobrio-wishlist-card" data-product-id="' + escapeHtml(product.id) +
        '" data-product-handle="' + escapeHtml(product.handle) + '">' +
        '<div class="mobrio-wishlist-card__media">' +
          '<a href="' + product.url + '" aria-label="' + escapeHtml(product.title) + '">' +
            (imageUrl ? '<img src="' + imageUrl + '" alt="' + escapeHtml(product.title) + '" loading="lazy">' : '') +
          '</a>' +
      '<button type="button" class="mobrio-wishlist-card__remove mobrio-wishlist-card__remove--heart" data-product-id="' +
        escapeHtml(product.id) + '" data-product-handle="' + escapeHtml(product.handle) +
        '" aria-label="' + escapeHtml(strings.remove) + '">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
          '<path class="heart-fill" d="M12 20.5c-.24 0-.47-.07-.67-.2C7.9 18 3.5 14.6 3.5 9.9 3.5 7 5.8 4.7 8.7 4.7c1.6 0 3.1.75 4.05 1.98.2.26.6.26.8 0C14.5 5.45 16 4.7 17.6 4.7c2.9 0 5.2 2.3 5.2 5.2 0 4.7-4.4 8.1-7.83 10.4-.2.13-.43.2-.67.2z"/>' +
          '<path class="heart-outline" d="M12 20.5c-.24 0-.47-.07-.67-.2C7.9 18 3.5 14.6 3.5 9.9 3.5 7 5.8 4.7 8.7 4.7c1.6 0 3.1.75 4.05 1.98.2.26.6.26.8 0C14.5 5.45 16 4.7 17.6 4.7c2.9 0 5.2 2.3 5.2 5.2 0 4.7-4.4 8.1-7.83 10.4-.2.13-.43.2-.67.2z" stroke-linejoin="round"/>' +
        '</svg>' +
      '</button>' + badge +
        '</div>' +
        '<div class="mobrio-wishlist-card__info">' +
          '<a href="' + product.url + '" class="mobrio-wishlist-card__title">' + escapeHtml(product.title) + '</a>' +
          '<div class="mobrio-wishlist-card__price">' + priceHtml + '</div>' +
          addButton +
        '</div>' +
      '</div>'
    );
  }

  P.renderPage = function () {
    var root = document.querySelector('[data-mobrio-wishlist-root]');
    if (!root) return Promise.resolve();

    var config = getConfig();
    var strings = config.strings || {
      soldOut: 'Agotado', addToCart: 'Añadir al carrito',
      chooseOptions: 'Elegir opciones', remove: 'Eliminar de favoritos', from: 'Desde'
    };

    var items = this.getItems();

    if (items.length === 0) {
      var tpl = document.getElementById('mobrio-wishlist-empty-template');
      root.innerHTML = tpl ? tpl.innerHTML : '';
      this._renderCount(document);
      return Promise.resolve();
    }

    root.setAttribute('aria-busy', 'true');
    var self = this;

    // Solo podemos pintar los que tienen handle (endpoint público lo exige).
    // Los que llegaron de la cuenta con id sin handle se omiten en el pintado
    // pero permanecen guardados; el Bloque B nos dará handle+id para todos.
    var fetches = items.map(function (entry) {
      if (entry.handle) return fetchProductByHandle(entry.handle);
      return resolveHandleById(entry.id);
    });

    return Promise.all(fetches).then(function (products) {
      var html = '<div class="mobrio-wishlist-grid">';
      var anyRendered = false;

      products.forEach(function (product, index) {
        if (!product) return; // eliminado, despublicado, o id-sin-handle
        // Completar el handle que faltara, ahora que lo conocemos.
        if (!items[index].handle && product.handle) {
          items[index].handle = product.handle;
        }
        html += buildCardHtml(product, strings);
        anyRendered = true;
      });

      html += '</div>';

      if (!anyRendered) {
        var emptyTpl = document.getElementById('mobrio-wishlist-empty-template');
        root.innerHTML = emptyTpl ? emptyTpl.innerHTML : '';
      } else {
        root.innerHTML = html;
        self._items = writeLocal(items); // persistimos handles recién resueltos
      }

      root.removeAttribute('aria-busy');
      self._renderCount(document);
    });
  };

  // -------------------------------------------------------------------------
  // Añadir al carrito desde la página de favoritos (respeta variantes)
  // -------------------------------------------------------------------------
  function bindAddToCartDelegation() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-add-variant-id]');
      if (!button || button.disabled) return;
      event.preventDefault();
      var variantId = button.getAttribute('data-add-variant-id');
      button.disabled = true;
      var original = button.textContent;
      button.textContent = '…';

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function (res) { return res.json(); })
        .then(function () {
          button.textContent = '✓';
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(function () { button.textContent = original; button.disabled = false; }, 1500);
        })
        .catch(function () { button.textContent = original; button.disabled = false; });
    });
  }

  // -------------------------------------------------------------------------
  // Delegación de clicks: un único listener para todos los botones
  // -------------------------------------------------------------------------
  function bindDelegatedClicks(instance) {
    // Fase de CAPTURA (tercer argumento = true): este listener corre ANTES
    // que el del enlace de la card de Dawn, así el corazón gana el click y
    // evitamos que .full-unstyled-link navegue al producto.
    document.addEventListener(
      'click',
      function (event) {
        var button = event.target.closest('.mobrio-wishlist-button, .mobrio-wishlist-card__remove');
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        var id = button.getAttribute('data-product-id');
        var handle = button.getAttribute('data-product-handle');

        // Caso especial: corazón dentro de la página de favoritos.
        // No repintamos: marcamos visualmente la card como quitada/re-añadida
        // y la card permanece hasta la próxima recarga (renderPage).
        if (button.classList.contains('mobrio-wishlist-card__remove')) {
          var nowActive = instance.toggle(id, handle);
          button.classList.toggle('is-removed', !nowActive);
          return;
        }

        instance.toggle(id, handle);
      },
      true
    );
  }

  function bindUpdateListener(instance) {
    document.addEventListener(EVENT_NAME, function (event) {
      instance.renderState(document);
      // En la página de favoritos NO repintamos ante cambios 'add'/'remove'
      // (esos vienen de pulsar el corazón de una card, y queremos que la card
      // se quede visible hasta recargar). Sí repintamos en 'sync'/'external-sync'
      // (carga inicial, otra pestaña), donde sí interesa refrescar.
      var action = event && event.detail ? event.detail.action : null;
      var onWishlistPage = document.querySelector('[data-mobrio-wishlist-root]');
      if (onWishlistPage && (action === 'sync' || action === 'external-sync' || action === 'clear')) {
        instance.renderPage();
      }
    });
  }

  // -------------------------------------------------------------------------
  // Init
  // -------------------------------------------------------------------------
  function init() {
    var instance = new MobrioWishlistClass();
    window.MobrioWishlist = instance;

    bindDelegatedClicks(instance);
    bindUpdateListener(instance);
    bindAddToCartDelegation();

    var start = function () {
      instance.renderState(document);
      if (document.querySelector('[data-mobrio-wishlist-root]')) instance.renderPage();
      instance.sync();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }

  init();
})();